import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';
import { Text } from './Text';
import { colors } from '@/theme/tokens';
import styles from './ConfirmSheet.module.css';

interface ConfirmSheetProps {
  open: boolean;
  /** What is about to happen, as a question or a plain statement. */
  title: string;
  /** One line saying what it changes — and, where it applies, what it does not. */
  message?: string;
  /** The thing being acted on, restated in the highlighted box. */
  subject?: ReactNode;
  /** The affirmative label. Say the action, never "ຕົກລົງ". */
  confirmLabel: string;
  /**
   * The way out. Not "ຍົກເລີກ" by default: half the things this sheet guards
   * are themselves called ຍົກເລີກ, and two buttons with that word on a sheet
   * asking whether to cancel something is exactly the wrong place to be vague.
   */
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Asks before an action the shopkeeper cannot simply tap again to undo.
 *
 * Retiring a product, reversing an expense, recording breakage, signing out —
 * each writes to the shop's books or drops a screen out from under whoever is
 * looking at it, and each sits one mis-tap away from a thumb that was aiming at
 * the button above. A sale does not come through here: it is the thing the shop
 * is here to do, it is already a deliberate two-screen flow, and a confirm on
 * every bill would be tapped through blind by the second morning.
 *
 * Same `<dialog>` shape as the other sheets — focus trap, Esc and `::backdrop`
 * for free, and supported at the Chrome 90 floor.
 */
export function ConfirmSheet({
  open,
  title,
  message,
  subject,
  confirmLabel,
  cancelLabel = 'ກັບຄືນ',
  busy = false,
  onConfirm,
  onClose,
}: ConfirmSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16}>
          {title}
        </Text>

        {subject ? <div className={styles.subject}>{subject}</div> : null}

        {message ? (
          <Text size={12} color={colors.textMuted}>
            {message}
          </Text>
        ) : null}

        <div className={styles.actions}>
          {/* The way out is first and on the left, so the thumb that taps
              without reading lands on the harmless one. */}
          <Button label={cancelLabel} variant="outline" className="grow" onPress={onClose} />
          <Button
            label={busy ? 'ກຳລັງບັນທຶກ…' : confirmLabel}
            className="grow"
            disabled={busy}
            onPress={onConfirm}
          />
        </div>
      </div>
    </dialog>
  );
}
